import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { UsuarioResposta } from '../api/usuario';
import type {
  LoginRequisicao,
  LoginResposta,
  RecuperarSenhaRequisicao,
  SolicitacaoSenhaResposta,
} from '../api/autenticacao';

export type { LoginRequisicao, LoginResposta };

const LS_TOKEN = 'igreja_auth_token';
const LS_NOME = 'igreja_auth_nome';
/** `AUTH-03` — instante absoluto, em epoch de milissegundos. */
const LS_EXPIRA = 'igreja_auth_expira_em';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly perfil$ = new BehaviorSubject<UsuarioResposta | null>(null);

  /** POST /autenticacao/login (backend igreja-api) */
  login(body: LoginRequisicao): Observable<LoginResposta> {
    return this.http.post<LoginResposta>(`${environment.api}/autenticacao/login`, body).pipe(
      tap((res) => this.setSession(res)),
      switchMap((res) =>
        this.http.get<UsuarioResposta>(`${environment.api}/autenticacao/usuario-logado`).pipe(
          tap((u) => this.perfil$.next(u)),
          catchError(() => {
            this.perfil$.next(null);
            return of(null);
          }),
          map(() => res),
        ),
      ),
    );
  }

  /**
   * POST /autenticacao/trocar-senha — sem corpo: o login é o do token. A API pede ao servidor de
   * autenticação um e-mail com o link; a página da troca é dele, não deste sistema.
   */
  trocarSenha(): Observable<SolicitacaoSenhaResposta> {
    return this.http.post<SolicitacaoSenhaResposta>(`${environment.api}/autenticacao/trocar-senha`, null);
  }

  /** POST /autenticacao/recuperar-senha — público: quem chama esqueceu a senha. */
  recuperarSenha(body: RecuperarSenhaRequisicao): Observable<SolicitacaoSenhaResposta> {
    return this.http.post<SolicitacaoSenhaResposta>(
      `${environment.api}/autenticacao/recuperar-senha`,
      body,
    );
  }

  /**
   * `AUTH-03` — toda leitura e escrita do armazenamento passa por aqui, em `try/catch`:
   * navegador com armazenamento bloqueado (janela privativa, política de site) degrada para
   * sessão em memória, que não sobrevive ao refresh, em vez de derrubar a aplicação.
   */
  private memoria: Record<string, string | null> = {};

  private gravar(chave: string, valor: string): void {
    this.memoria[chave] = valor;
    try {
      localStorage.setItem(chave, valor);
    } catch {
      // Armazenamento bloqueado: a sessão vale só enquanto a aba estiver aberta.
    }
  }

  private ler(chave: string): string | null {
    try {
      return localStorage.getItem(chave) ?? this.memoria[chave] ?? null;
    } catch {
      return this.memoria[chave] ?? null;
    }
  }

  private apagar(chave: string): void {
    delete this.memoria[chave];
    try {
      localStorage.removeItem(chave);
    } catch {
      // Nada a limpar: o armazenamento nunca chegou a receber o valor.
    }
  }

  /**
   * `AUTH-03` — `expiraEmSegundos` é duração, e duração não sobrevive a fechar a aba: guarda-se
   * `Date.now() + segundos * 1000`, que é instante absoluto.
   *
   * Sem prazo publicado, a chave é **apagada**, não zerada: ausência de prazo não é prazo
   * vencido. Gravar zero expulsaria do sistema quem acabou de entrar.
   */
  setSession(res: LoginResposta): void {
    if (res.token) this.gravar(LS_TOKEN, res.token);
    if (res.nome) this.gravar(LS_NOME, res.nome);

    const segundos = res.expiraEmSegundos;
    if (typeof segundos === 'number' && segundos > 0) {
      this.gravar(LS_EXPIRA, String(Date.now() + segundos * 1000));
    } else {
      this.apagar(LS_EXPIRA);
    }
  }

  /** Instante de expiração, ou `null` quando o contrato não publicou prazo. */
  private get expiraEm(): number | null {
    const bruto = this.ler(LS_EXPIRA);
    if (!bruto) return null;
    const valor = Number(bruto);
    return Number.isFinite(valor) ? valor : null;
  }

  getToken(): string | null {
    return this.ler(LS_TOKEN);
  }

  getNome(): string | null {
    return this.ler(LS_NOME);
  }

  /**
   * `AUTH-07` — token com prazo vencido conta como **não autenticado**, mesmo antes de a API
   * recusar. Sem prazo publicado, vale o que valia antes: a sessão dura até o `401`.
   */
  isAuthenticated(): boolean {
    const t = this.getToken();
    if (!t) {
      return false;
    }
    const prazo = this.expiraEm;
    return prazo === null || prazo > Date.now();
  }

  getPerfil(): UsuarioResposta | null {
    return this.perfil$.value;
  }

  /** Garante perfil carregado quando há token (ex.: F5). */
  ensurePerfil(): Observable<UsuarioResposta | null> {
    if (!this.isAuthenticated()) {
      this.perfil$.next(null);
      return of(null);
    }
    if (this.perfil$.value) return of(this.perfil$.value);
    return this.http.get<UsuarioResposta>(`${environment.api}/autenticacao/usuario-logado`).pipe(
      tap((u) => this.perfil$.next(u)),
      catchError(() => {
        this.perfil$.next(null);
        return of(null);
      }),
    );
  }

  reloadPerfil(): Observable<UsuarioResposta | null> {
    if (!this.isAuthenticated()) {
      this.perfil$.next(null);
      return of(null);
    }
    return this.http.get<UsuarioResposta>(`${environment.api}/autenticacao/usuario-logado`).pipe(
      tap((u) => this.perfil$.next(u)),
      catchError(() => of(null)),
    );
  }

  clearSession(): void {
    this.apagar(LS_TOKEN);
    this.apagar(LS_NOME);
    this.apagar(LS_EXPIRA);
    this.perfil$.next(null);
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  isAdmin(): boolean {
    return !!this.perfil$.value?.admin;
  }

  isCoordenacao(): boolean {
    return !!this.perfil$.value?.coordenacao;
  }

  isCatequista(): boolean {
    return !!this.perfil$.value?.catequista;
  }

  /** Acesso às telas de gestão (matrículas, pessoas, turmas). */
  podeGestao(): boolean {
    const p = this.perfil$.value;
    // CTR-06: os perfis são opcionais no contrato, então ausente conta como falso.
    return !!p && !!(p.admin || p.coordenacao);
  }

  podeVerMinhasTurmasMenu(): boolean {
    const p = this.perfil$.value;
    return !!p?.catequista;
  }
}
